"use client";

import { updateTheme, type Settings } from "@/lib/settings-api";
import { useTheme } from "@/lib/store";

interface Props {
  settings: Settings | null;
  onUpdate: (s: Settings) => void;
}

const THEMES = [
  { id: "warm", label: "Nature", description: "Forest & sage tones" },
  { id: "minimal", label: "Minimal", description: "Clean & focused" },
  { id: "modern", label: "Modern", description: "Bold & vibrant" },
];

export default function ThemeSection({ settings, onUpdate }: Props) {
  const { flowMode, setFlowMode } = useTheme();

  const handleSelect = async (mode: string) => {
    setFlowMode(mode as "warm" | "minimal" | "modern");

    try {
      const updated = await updateTheme(mode);
      onUpdate(updated);
    } catch {
      // Ignore
    }
  };

  return (
    <div
      className="bg-card rounded-2xl border border-border shadow-sm p-4"
      data-testid="settings-theme-section"
    >
      <h2 className="text-sm font-semibold text-text-light mb-3">Visual Style</h2>
      <div className="flex gap-2">
        {THEMES.map((theme) => (
          <button
            key={theme.id}
            onClick={() => handleSelect(theme.id)}
            data-testid={`settings-theme-${theme.id}`}
            aria-pressed={flowMode === theme.id}
            className={`flex-1 py-3 rounded-xl border text-center transition-all ${
              flowMode === theme.id
                ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                : "border-border hover:border-primary"
            }`}
          >
            <p className="text-xs font-semibold text-text">{theme.label}</p>
            <p className="text-[10px] text-muted mt-0.5">{theme.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
